import { useCallback, useId, useRef, useState } from 'react'
import { useTranslation } from '../../lib/i18n'
import {
  QUIZ_QUESTIONS,
  TOOL_LINK,
  isComplete,
  scoreQuiz,
} from '../../lib/coding-tool-quiz'
import type { ToolId } from '../../lib/coding-tool-quiz'

/**
 * Fully client-side "Which AI Coding Tool Should I Use?" quiz.
 *
 * Steps through the shared question set ({@link ../../lib/coding-tool-quiz.ts})
 * one question at a time as a radiogroup, lets the reader go back and change any
 * answer, then scores deterministically with {@link scoreQuiz} and shows the
 * recommended tool, a short rationale and a deep link to the most relevant
 * comparison page. Nothing leaves the browser: no sign-up, no API, no tracking.
 *
 * SSR-safe: the default state is deterministic and identical on server and
 * client (empty answers, step 0; no Date.now/Math.random in render). Accessible:
 * each question is a labelled radiogroup, the step heading is focused on
 * navigation for screen-reader users, the result is announced via aria-live, and
 * motion respects prefers-reduced-motion (handled in CSS). All copy is read from
 * the dictionary so it renders natively in EN and DE. Mounted by the tool detail
 * page via the `ai-coding-tool-quiz` toolKey.
 */

const TOTAL = QUIZ_QUESTIONS.length

export function CodingToolQuiz() {
  const { dict, path } = useTranslation()
  const ui = dict.pages.toolWidgets.codingQuiz
  const q = ui.questions
  const tools = ui.tools

  // Deterministic initial state: no answers, on the first step, no result shown.
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [step, setStep] = useState(0)
  const [showResult, setShowResult] = useState(false)

  const baseId = useId()
  // Focused on step change so keyboard / screen-reader users land on the new
  // question (or the result) rather than staying on the button they clicked.
  const stepHeadingRef = useRef<HTMLHeadingElement>(null)
  const resultHeadingRef = useRef<HTMLHeadingElement>(null)

  const complete = isComplete(answers)
  const question = QUIZ_QUESTIONS[step]
  const questionCopy = q[question.id as keyof typeof q]

  const focusSoon = useCallback((el: HTMLElement | null) => {
    // Defer to the next frame so the element exists after the state update.
    if (!el) return
    requestAnimationFrame(() => el.focus())
  }, [])

  const select = (optionId: string) => {
    const next = { ...answers, [question.id]: optionId }
    setAnswers(next)
    // Auto-advance to keep the flow snappy; the last answer reveals the result.
    if (step < TOTAL - 1) {
      setStep(step + 1)
      focusSoon(stepHeadingRef.current)
    } else if (isComplete(next)) {
      setShowResult(true)
      focusSoon(resultHeadingRef.current)
    }
  }

  const goBack = () => {
    if (showResult) {
      setShowResult(false)
      focusSoon(stepHeadingRef.current)
      return
    }
    if (step > 0) {
      setStep(step - 1)
      focusSoon(stepHeadingRef.current)
    }
  }

  const reset = () => {
    setAnswers({})
    setStep(0)
    setShowResult(false)
    focusSoon(stepHeadingRef.current)
  }

  const seeResult = () => {
    if (!complete) return
    setShowResult(true)
    focusSoon(resultHeadingRef.current)
  }

  const progressPct = Math.round((step / TOTAL) * 100)

  if (showResult && complete) {
    const result = scoreQuiz(answers)
    const winner: ToolId = result.winner
    const toolCopy = tools[winner]
    const compareHref = path('comparison', {
      comparisonSlug: TOOL_LINK[winner].comparisonSlug,
    })
    // Runner-up: the next-highest distinct tool, for an honest "also consider".
    const ranked = [...Object.entries(result.scores)] as Array<[ToolId, number]>
    ranked.sort((a, b) => b[1] - a[1])
    const runnerUp = ranked.find(([id]) => id !== winner)
    const runnerUpCopy = runnerUp ? tools[runnerUp[0]] : null

    return (
      <div className="tool-widget">
        <div className="tool-quiz-result" aria-live="polite">
          <span className="tool-quiz-result-eyebrow">{ui.resultEyebrow}</span>
          <h3 className="tool-quiz-result-name" ref={resultHeadingRef} tabIndex={-1}>
            {toolCopy.name}
          </h3>
          <p className="tool-quiz-result-rationale">{toolCopy.rationale}</p>
          {runnerUpCopy ? (
            <p className="tool-quiz-runner-up">
              {ui.runnerUpLabel.replace('{tool}', runnerUpCopy.name)}
            </p>
          ) : null}
          <div className="tool-quiz-result-actions">
            <a className="tool-quiz-cta" href={compareHref}>
              {ui.seeComparison.replace('{tool}', toolCopy.name)}
            </a>
            <button type="button" className="tool-quiz-secondary" onClick={goBack}>
              {ui.changeLast}
            </button>
            <button type="button" className="tool-quiz-secondary" onClick={reset}>
              {ui.restart}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="tool-widget">
      <div className="tool-quiz">
        <div className="tool-quiz-progress">
          <div
            className="tool-quiz-progress-bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={TOTAL}
            aria-valuenow={step}
            aria-label={ui.progressLabel
              .replace('{current}', String(step + 1))
              .replace('{total}', String(TOTAL))}
          >
            <span
              className="tool-quiz-progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="tool-quiz-progress-text">
            {ui.progressLabel
              .replace('{current}', String(step + 1))
              .replace('{total}', String(TOTAL))}
          </span>
        </div>

        <fieldset className="tool-quiz-fieldset">
          <legend className="tool-quiz-legend">
            <h3
              className="tool-quiz-question"
              ref={stepHeadingRef}
              tabIndex={-1}
            >
              {questionCopy.title}
            </h3>
          </legend>
          <div
            className="tool-quiz-options"
            role="radiogroup"
            aria-label={questionCopy.title}
          >
            {question.options.map((option) => {
              const checked = answers[question.id] === option.id
              const optionCopy =
                questionCopy.options[
                  option.id as keyof typeof questionCopy.options
                ]
              const id = `${baseId}-${question.id}-${option.id}`
              return (
                <button
                  type="button"
                  key={option.id}
                  id={id}
                  role="radio"
                  aria-checked={checked}
                  className={`tool-quiz-option${checked ? ' is-selected' : ''}`}
                  onClick={() => select(option.id)}
                >
                  <span className="tool-quiz-option-marker" aria-hidden="true" />
                  <span className="tool-quiz-option-text">{optionCopy}</span>
                </button>
              )
            })}
          </div>
        </fieldset>

        <div className="tool-quiz-nav">
          <button
            type="button"
            className="tool-quiz-secondary"
            onClick={goBack}
            disabled={step === 0}
          >
            {ui.back}
          </button>
          {complete ? (
            <button type="button" className="tool-quiz-cta" onClick={seeResult}>
              {ui.seeResult}
            </button>
          ) : (
            <button type="button" className="tool-quiz-secondary" onClick={reset}>
              {ui.reset}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
